/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import cbStyles from "./SimpleTableCheckBox.module.css";
import styles from "./SimpleTableColumnFilter.module.css";
import { useSimpleTableContext } from "./SimpleTableContext";
import { CloseSvg } from "./Svgs";
import { ISimpleTableColumnFilter } from "./interface";

enum ESimpleTableColumnFilterStatus {
  FilteredOutByOtherFilters = "filteredOutByOtherFilters",
  AvailableNoLocalFilter = "availableNoLocalFilter",
  AvailableButNotInLocalFilter = "availableButNotInLocalFilter",
  AvailableAndInLocalFilter = "availableAndInLocalFilter",
}

interface ISimpleTableColumnFilterStatus {
  value: string;
  selected: boolean;
  status: ESimpleTableColumnFilterStatus;
}

export const SimpleTableColumnFilter = ({ columnName }: { columnName: string }) => {
  const simpleTableContext = useSimpleTableContext();
  const allCheck = useRef<HTMLInputElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  /** Value of the local search input */
  const [localFilterText, setLocalFilterText] = useState<string>("");
  /** Filter for this column */
  const thisFilter = simpleTableContext.currentColumnFilters?.find(
    (cf) => cf.columnName === columnName,
  );

  /**
   *  Current state of the values for this column
   *  @param filterItems - All unique items for this column with their current selected and available status
   *  @param setFilterItems - Function to update the filter items, which will update the checkboxes and the filtered list
   */
  const [filterItems, setFilterItems] = useState<ISimpleTableColumnFilterStatus[]>(
    (
      simpleTableContext.allColumnItems.find((cf) => cf.columnName === columnName)?.values ?? []
    ).map((value) => {
      const isAvailable = simpleTableContext.currentColumnItems
        .find((cf) => cf.columnName === columnName)
        ?.values.includes(value);
      const isSelected = thisFilter ? thisFilter.values.includes(value) : true;
      return {
        value,
        selected: isSelected,
        status: !isAvailable
          ? ESimpleTableColumnFilterStatus.FilteredOutByOtherFilters
          : ESimpleTableColumnFilterStatus.AvailableNoLocalFilter,
      };
    }),
  );

  /**
   * onClose handler for the filter popover, applies the current filter to the table and resets the local filter state
   */
  const onClose = (values?: string[]) => {
    const newColumnFilters = [...simpleTableContext.currentColumnFilters];
    const ix = simpleTableContext.currentColumnFilters.findIndex(
      (cf) => cf.columnName === columnName,
    );
    // If the current filter is the same as the available list, remove it
    if (filterItems.every((fi) => fi.selected) && ix > -1) {
      newColumnFilters.splice(ix, 1);
    } else {
      const newColumnFilter: ISimpleTableColumnFilter = {
        columnName,
        values: values ?? filterItems.filter((fi) => fi.selected).map((fi) => fi.value),
      };
      if (ix > -1) {
        newColumnFilters.splice(ix, 1, newColumnFilter);
      } else {
        newColumnFilters.push(newColumnFilter);
      }
    }
    simpleTableContext.setCurrentColumnFilters(newColumnFilters);
    simpleTableContext.setCurrentColumnFilter(null);
    setLocalFilterText("");
  };

  // Handle typing into the filter box
  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalFilterText(e.currentTarget.value);
    const newFilter = e.currentTarget.value.trim();
    const newFilterItems = filterItems.map((fi) => {
      if (fi.status === ESimpleTableColumnFilterStatus.FilteredOutByOtherFilters) {
        return {
          ...fi,
          selected: fi.value.toLowerCase().includes(newFilter.toLowerCase()),
        };
      } else if (newFilter !== "") {
        const thisValueMatch = fi.value.toLowerCase().includes(newFilter.toLowerCase());
        return {
          ...fi,
          selected: thisValueMatch,
          status: thisValueMatch
            ? ESimpleTableColumnFilterStatus.AvailableAndInLocalFilter
            : ESimpleTableColumnFilterStatus.AvailableButNotInLocalFilter,
        };
      } else {
        return {
          ...fi,
          status: ESimpleTableColumnFilterStatus.AvailableNoLocalFilter,
        };
      }
    });
    setFilterItems(newFilterItems);
  };

  // Toggle all viewed rows
  const handleCheckAll = () => {
    // Add all items if they are not already in the current filter
    if (filterItems.some((fi) => !fi.selected)) {
      setFilterItems(filterItems.map((fi) => ({ ...fi, selected: true })));
    } else {
      setFilterItems(filterItems.map((fi) => ({ ...fi, selected: false })));
    }
  };

  // Toggle all viewed rows
  const handleCheckSearch = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.stopPropagation();
    e.preventDefault();
    if (localFilterText.trim() === "") return;
    const trimmedFilterText = localFilterText.trim().toLowerCase();
    const newMatchSearch = filterItems.some(
      (fi) =>
        fi.status === ESimpleTableColumnFilterStatus.AvailableAndInLocalFilter && !fi.selected,
    );
    setFilterItems(
      filterItems.map((fi) => {
        let newSelected: boolean;
        if (newMatchSearch) {
          if (fi.status === ESimpleTableColumnFilterStatus.AvailableAndInLocalFilter) {
            newSelected = true;
          } else if (fi.status === ESimpleTableColumnFilterStatus.AvailableButNotInLocalFilter) {
            newSelected = false;
          } else {
            newSelected = fi.value.toLowerCase().includes(trimmedFilterText);
          }
        } else if (fi.status === ESimpleTableColumnFilterStatus.AvailableButNotInLocalFilter) {
          newSelected = true;
        } else if (fi.status === ESimpleTableColumnFilterStatus.AvailableAndInLocalFilter) {
          newSelected = false;
        } else {
          newSelected = !fi.value.toLowerCase().includes(trimmedFilterText);
        }

        return {
          ...fi,
          selected: newSelected,
        };
      }),
    );
  };

  // Toggle a single row
  const handleCheckRow = (
    e: React.MouseEvent<HTMLElement, MouseEvent> | React.ChangeEvent<HTMLInputElement>,
    v: string,
  ) => {
    e.stopPropagation();
    e.preventDefault();
    setFilterItems(
      filterItems.map((fi) =>
        fi.value === v
          ? {
              ...fi,
              selected: !fi.selected,
            }
          : fi,
      ),
    );
  };

  const BodyRow = (props: {
    fi: ISimpleTableColumnFilterStatus;
    ix: number;
    colour?: string;
  }): React.ReactElement => (
    <tr>
      <td style={{ backgroundColor: props.colour }}>
        <div
          className={cbStyles.checkboxContainer}
          onClick={(e) => handleCheckRow(e, props.fi.value)}
        >
          <input
            aria-label={props.fi.value}
            id={`${simpleTableContext.id}-columnfilter-${columnName}-check-${props.ix}`}
            type="checkbox"
            role="checkbox"
            className={simpleTableContext.filterCheckClassName}
            checked={props.fi.selected}
            onChange={
              // istanbul ignore next -- this element is hidden
              (e) => handleCheckRow(e, props.fi.value)
            }
          />
        </div>
      </td>
      <td style={{ backgroundColor: props.colour }}>{props.fi.value}</td>
    </tr>
  );

  /**
   * Focus the search input when the filter is opened, and refocus if it is closed and reopened
   */
  useEffect(() => {
    const attemptFocus = () => {
      if (inputRef.current && document.activeElement !== inputRef.current) {
        inputRef.current.focus();
      }
    };

    attemptFocus();
    const timer1 = setTimeout(attemptFocus, 50);
    const timer2 = setTimeout(attemptFocus, 150);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, []);

  /**
   * If enter is pressed while the filter is open, apply the filter
   */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  /**
   * Manage the state of the select all checkbox based on the current filter and available list
   */
  useEffect(() => {
    /* istanbul ignore else */
    if (allCheck.current) {
      if (
        filterItems.every(
          (fi) =>
            fi.selected || fi.status === ESimpleTableColumnFilterStatus.FilteredOutByOtherFilters,
        )
      ) {
        allCheck.current.checked = true;
        allCheck.current.indeterminate = false;
      } else if (filterItems.some((fi) => fi.selected)) {
        allCheck.current.checked = false;
        allCheck.current.indeterminate = true;
      } else {
        allCheck.current.checked = false;
        allCheck.current.indeterminate = false;
      }
    }
  }, [filterItems, localFilterText]);

  const topItems = filterItems.filter(
    (fi) =>
      fi.status === ESimpleTableColumnFilterStatus.AvailableAndInLocalFilter ||
      fi.status === ESimpleTableColumnFilterStatus.AvailableNoLocalFilter,
  );
  const middleItems = filterItems.filter(
    (fi) => fi.status === ESimpleTableColumnFilterStatus.AvailableButNotInLocalFilter,
  );
  const bottomItems = filterItems.filter(
    (fi) => fi.status === ESimpleTableColumnFilterStatus.FilteredOutByOtherFilters,
  );
  const selectedCount = filterItems.filter((fi) => fi.selected).length;

  return (
    <table className={styles.table}>
      <thead>
        <tr>
          <th>&nbsp;</th>
          <th>
            <div className={styles.search}>
              <input
                ref={inputRef}
                id={`${simpleTableContext.id}-columnfilter-${columnName}-filter`}
                aria-label="Column filter search"
                value={localFilterText}
                onChange={handleFilterChange}
              />
              <button
                className={styles.toggleButton}
                onClick={handleCheckSearch}
                aria-label="Toggle search results"
                title="Toggle search results"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  height="16px"
                  viewBox="0 -960 960 960"
                  width="16px"
                  fill="#1f1f1f"
                >
                  <path d="m360-240 56-56-62-64h166v-80H354l62-64-56-56-160 160 160 160Zm240-160 160-160-160-160-56 56 62 64H440v80h166l-62 64 56 56ZM480-80q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm0-320Z" />
                </svg>
              </button>
              <div className={styles.close}>
                <CloseSvg onClick={() => onClose()} />
              </div>
            </div>
          </th>
        </tr>
        <tr>
          <th className={styles.boxHeader}>
            <div
              className={cbStyles.checkboxContainer}
              onClick={() => handleCheckAll()}
            >
              <input
                id={`${simpleTableContext.id}-columnfilter-${columnName}-check-all`}
                aria-label="Select all"
                className={simpleTableContext.filterCheckClassName}
                ref={allCheck}
                type="checkbox"
                role="checkbox"
              />
            </div>
          </th>
          <th>Select all</th>
        </tr>
      </thead>

      <tbody className={styles.scroll}>
        {topItems.length === 0 ? (
          <tr>
            <td
              colSpan={2}
              style={{ textAlign: "center" }}
            >
              No matching items
            </td>
          </tr>
        ) : (
          topItems.map((item, ix) => (
            <BodyRow
              key={item.value}
              ix={ix}
              fi={item}
            />
          ))
        )}
        {middleItems.length > 0 && (
          <>
            <tr>
              <th
                colSpan={2}
                style={{ backgroundColor: "lightgrey" }}
              ></th>
            </tr>
            {middleItems.map((item, ix) => (
              <BodyRow
                key={item.value}
                ix={ix}
                fi={item}
                colour="lightgrey"
              />
            ))}
          </>
        )}
        {bottomItems.length > 0 && (
          <>
            <tr>
              <th
                colSpan={2}
                style={{ backgroundColor: "darkgrey", height: "2px", textAlign: "left" }}
              >
                <small>Hidden by other filters</small>
              </th>
            </tr>
            {bottomItems.map((item, ix) => (
              <BodyRow
                key={item.value}
                ix={ix}
                fi={item}
                colour="darkgrey"
              />
            ))}
          </>
        )}
      </tbody>
      <tfoot>
        <tr>
          <td colSpan={2}>
            <small>{`${selectedCount} item${selectedCount !== 1 ? "s" : ""} selected`}</small>
          </td>
        </tr>
      </tfoot>
    </table>
  );
};

SimpleTableColumnFilter.displayName = "SimpleTableColumnFilter";
